import React from "react";

import {
  faKey,
  faPlus,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { Cell, ColumnDef } from "@tanstack/react-table";
import { twJoin } from "tailwind-merge";

import { KeyType } from "src/apiKeys/KeyType";
import { Card } from "src/base-components/Card";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { DisplayDateFromNow, DisplayDateTime } from "src/base-components/Date";
import { TableComp as Table } from "src/base-components/Table";
import {
  ApiKeyType,
  ServiceAccountOAuthClientInfo,
  ServiceAccountOAuthClientPatchStatusEnum,
} from "src/clients/taktile-api";
import { RawCell } from "src/dataTable/RawCell";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { Pill } from "src/design-system/Pill";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { SubHeader } from "src/flow/SubHeader";
import { useCapabilities } from "src/hooks/useCapabilities";
import { ColumnHeader } from "src/jobs/common/TableElements";
import { DashboardContent } from "src/layout/DashboardContent";
import {
  useSetOAuthClientStatus,
  useWorkspaceOAuthClients,
} from "src/oauthClients/api/queries";
import {
  CreatedOAuthClient,
  CreateOAuthClientModal,
} from "src/oauthClients/CreateOAuthClientModal";
import { proxyTokenEndpointFor } from "src/oauthClients/constants";
import { DeleteOAuthClientModal } from "src/oauthClients/DeleteOAuthClientModal";
import { OAuthClientActions } from "src/oauthClients/OAuthClientActions";
import { RenameOAuthClientModal } from "src/oauthClients/RenameOAuthClientModal";
import { RotateOAuthClientKeyModal } from "src/oauthClients/RotateOAuthClientKeyModal";
import {
  OAuthClientCredentials,
  ShowOAuthClientCredentialsModal,
} from "src/oauthClients/ShowOAuthClientCredentialsModal";
import { errorMessage } from "src/utils/stringUtils";
import { useWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The client keeps minting tokens until its LAST key expires, so the deadline a
 * customer has to act on is the furthest expiry, not the nearest.
 */
const daysUntilLastKeyExpires = (
  client: ServiceAccountOAuthClientInfo,
): number | null => {
  const expiries = client.keys.map((key) => new Date(key.expires_at).getTime());
  if (expiries.length === 0) return null;
  return Math.ceil((Math.max(...expiries) - Date.now()) / DAY_MS);
};

/**
 * ColumnHeader insets by 1.5 while RawCell insets its values by 2, so a header
 * rendered bare sits half a step left of its own column. Close that gap here and
 * let each column state how its values are aligned, so the two always agree.
 */
const Header: React.FC<{ header?: string; align?: "left" | "center" }> = ({
  header,
  align = "left",
}) => (
  <ColumnHeader>
    <div
      className={twJoin(
        "w-full pl-0.5",
        align === "center" ? "text-center" : "text-left",
      )}
    >
      {header}
    </div>
  </ColumnHeader>
);

/** Aligns a hand-rolled cell with the inset RawCell gives the other columns. */
const AlignedCell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex min-h-[48px] items-center gap-x-2 pl-2">{children}</div>
);

const KeysCell: React.FC<{ client: ServiceAccountOAuthClientInfo }> = ({
  client,
}) => (
  <div className="flex min-h-[48px] flex-col items-center justify-center gap-0.5 px-2 py-2">
    {client.keys.map((key) => (
      <Tooltip
        key={key.kid}
        placement="top"
        title={
          key.last_used_at ? (
            <>
              Last used <DisplayDateTime value={key.last_used_at} />
            </>
          ) : (
            "Never used"
          )
        }
      >
        <span className="break-all font-code-12 text-gray-800">{key.kid}</span>
      </Tooltip>
    ))}
  </div>
);

type CallbackWithClient = (client: ServiceAccountOAuthClientInfo) => void;

const buildColumns = (
  onRename: CallbackWithClient,
  onRotate: CallbackWithClient,
  onToggleStatus: CallbackWithClient,
  onDelete: CallbackWithClient,
): ColumnDef<ServiceAccountOAuthClientInfo>[] => [
  {
    id: "clientName",
    header: () => <Header header="Client name" />,
    accessorFn: (row) => row.client_name,
    cell: ({ cell }) => (
      <RawCell<ServiceAccountOAuthClientInfo>
        cell={<span>{cell.renderValue<string>()}</span>}
        classNameOverrides="min-h-[48px] max-w-0"
      />
    ),
  },
  {
    // The client_id is shown once, in the dialog right after creation. Without
    // it here an operator cannot tell which row a live integration runs as.
    id: "clientId",
    header: () => <Header header="Client ID" />,
    accessorFn: (row) => row.client_id,
    cell: ({ cell }) => {
      const clientId = cell.renderValue<string>() ?? "";
      return (
        <AlignedCell>
          <span className="break-all font-code-12 text-gray-800">
            {clientId}
          </span>
          <CopyTextIcon
            dataLoc="oauth-client-copy-client-id"
            feedback="inline"
            tooltip="Copy client ID"
            value={clientId}
          />
        </AlignedCell>
      );
    },
  },
  {
    id: "keyTypes",
    header: () => <Header header="Permissions" />,
    accessorFn: (row) => row.key_types,
    cell: ({ cell }) => (
      <AlignedCell>
        <KeyType value={cell.getValue() as ApiKeyType[]} />
      </AlignedCell>
    ),
  },
  {
    id: "status",
    header: () => <Header header="Status" />,
    accessorFn: (row) => row.status,
    cell: ({ cell }) => {
      const isActive = cell.getValue<string>() === "active";
      return (
        <AlignedCell>
          <Pill size="sm" variant={isActive ? "green" : "gray"}>
            <Pill.Text>{isActive ? "Active" : "Disabled"}</Pill.Text>
          </Pill>
        </AlignedCell>
      );
    },
  },
  {
    id: "sponsor",
    header: () => <Header header="Created by" />,
    accessorFn: (row) => row.sponsored_by_username ?? "",
    cell: ({ cell }) => {
      const isDeactivated = cell.row.original.sponsored_by_is_active === false;
      return (
        <AlignedCell>
          <span
            className={twJoin(
              "font-inter-medium-12px",
              isDeactivated && "text-gray-500",
            )}
          >
            {cell.getValue<string>()}
          </span>
          {isDeactivated && (
            <Pill
              dataLoc="creator-deactivated"
              fontType="text"
              size="sm"
              variant="gray"
            >
              <Pill.Text>Deactivated</Pill.Text>
            </Pill>
          )}
        </AlignedCell>
      );
    },
  },
  {
    id: "keyExpiry",
    header: () => <Header header="Key expiry" />,
    accessorFn: (row) => daysUntilLastKeyExpires(row),
    cell: ({ cell }) => {
      const days = cell.getValue<number | null>();
      if (days === null) {
        return (
          <RawCell<ServiceAccountOAuthClientInfo>
            cell={<span>No keys</span>}
            classNameOverrides="min-h-[48px]"
          />
        );
      }
      return (
        <RawCell<ServiceAccountOAuthClientInfo>
          cell={
            <span className={twJoin(days <= 30 && "text-red-600")}>
              {days <= 0 ? "Expired" : `${days} days`}
            </span>
          }
          classNameOverrides="min-h-[48px]"
        />
      );
    },
  },
  {
    id: "keys",
    header: () => <Header align="center" header="Keys" />,
    cell: ({ cell }) => <KeysCell client={cell.row.original} />,
  },
  {
    id: "createdAt",
    header: () => <Header header="Created at" />,
    accessorFn: (row) => row.created_at,
    cell: ({ cell }) => {
      const isoDate = (
        cell as Cell<ServiceAccountOAuthClientInfo, string>
      ).getValue();
      return (
        <Tooltip placement="top" title={<DisplayDateTime value={isoDate} />}>
          <RawCell<ServiceAccountOAuthClientInfo>
            cell={<DisplayDateFromNow value={isoDate} />}
            classNameOverrides="min-h-[48px]"
          />
        </Tooltip>
      );
    },
  },
  {
    id: "actions",
    header: () => <Header />,
    cell: (cell) => {
      const client = cell.row.original;
      return (
        <OAuthClientActions
          isActive={client.status === "active"}
          onDeleteClick={() => onDelete(client)}
          onRenameClick={() => onRename(client)}
          onRotateClick={() => onRotate(client)}
          onToggleStatusClick={() => onToggleStatus(client)}
        />
      );
    },
  },
];

type Props = {
  organizationId: string;
  workspaceId: string;
};

export const OAuthClientsOverview: React.FC<Props> = ({
  organizationId,
  workspaceId,
}) => {
  const { apiKeys } = useCapabilities();
  const { workspace } = useWorkspaceAndOrg();
  const { isLoading, data, isError, error } = useWorkspaceOAuthClients(
    organizationId,
    workspaceId,
  );
  const setStatus = useSetOAuthClientStatus(organizationId, workspaceId);

  const createModal = useModal();
  const renameModal = useModal<ServiceAccountOAuthClientInfo>();
  const rotateModal = useModal<ServiceAccountOAuthClientInfo>();
  const deleteModal = useModal<ServiceAccountOAuthClientInfo>();
  const credentialsModal = useModal<OAuthClientCredentials>();

  const proxyTokenEndpoint = workspace?.base_url
    ? proxyTokenEndpointFor(workspace.base_url)
    : "";

  const handleToggleStatus = async (client: ServiceAccountOAuthClientInfo) => {
    const isDisabling = client.status === "active";
    try {
      await setStatus.mutateAsync({
        clientId: client.client_id,
        status: isDisabling
          ? ServiceAccountOAuthClientPatchStatusEnum.DISABLED
          : ServiceAccountOAuthClientPatchStatusEnum.ACTIVE,
      });
    } catch (caught) {
      toastActions.failure({
        title: `Failed to ${isDisabling ? "disable" : "enable"} the OAuth client`,
        description: errorMessage(caught),
      });
    }
  };

  const handleCreated = ({ client, privateKeyPem }: CreatedOAuthClient) =>
    credentialsModal.openModal({
      clientName: client.client_name,
      clientId: client.client_id,
      kid: client.kid,
      tokenEndpoint: client.proxy_token_endpoint,
      privateKeyPem,
    });

  const columns = buildColumns(
    renameModal.openModal,
    rotateModal.openModal,
    handleToggleStatus,
    deleteModal.openModal,
  );

  // The list query answers with the client the rotate modal is showing, so read
  // the live row back out of it rather than the snapshot the modal opened with.
  const rotatingClient = data?.find(
    (client) => client.client_id === rotateModal.data?.client_id,
  );

  const renderContent = () => {
    if (isError) {
      return (
        <EmptyState
          dataLoc="oauth-clients-error-state"
          description={errorMessage(error)}
          headline="Error loading OAuth clients"
          icon={faTriangleExclamation}
          variant="error"
        />
      );
    }

    if (!isLoading && data?.length === 0) {
      return (
        <EmptyState
          dataLoc="oauth-clients-empty-state"
          description="Create an OAuth client to let your system mint short-lived Taktile tokens with a key pair you control."
          headline="No OAuth clients yet"
          icon={faKey}
        />
      );
    }

    return <Table columns={columns} data={data ?? []} isLoading={isLoading} />;
  };

  return (
    <DashboardContent
      Header={
        <SubHeader title="OAuth clients">
          {apiKeys.canAccess && (
            <SubHeader.Button
              dataLoc="create-oauth-client"
              icon={faPlus}
              tooltip="Create OAuth client"
              onClick={() => createModal.openModal()}
            />
          )}
        </SubHeader>
      }
    >
      <Card className="flex h-full flex-col pt-1" variant="inverted" fullWidth>
        <Card.Content className="min-h-0 flex-1">
          {renderContent()}
        </Card.Content>
      </Card>
      <CreateOAuthClientModal
        open={createModal.isOpen}
        organizationId={organizationId}
        proxyTokenEndpoint={proxyTokenEndpoint}
        workspaceId={workspaceId}
        onClose={createModal.closeModal}
        onCreated={handleCreated}
      />
      <ShowOAuthClientCredentialsModal
        afterLeave={credentialsModal.afterLeave}
        credentials={credentialsModal.data}
        open={credentialsModal.isOpen}
        onClose={credentialsModal.closeModal}
      />
      <RenameOAuthClientModal
        afterLeave={renameModal.afterLeave}
        client={renameModal.data}
        open={renameModal.isOpen}
        organizationId={organizationId}
        workspaceId={workspaceId}
        onClose={renameModal.closeModal}
      />
      <RotateOAuthClientKeyModal
        afterLeave={rotateModal.afterLeave}
        client={rotatingClient}
        open={rotateModal.isOpen}
        organizationId={organizationId}
        workspaceId={workspaceId}
        onClose={rotateModal.closeModal}
      />
      <DeleteOAuthClientModal
        afterLeave={deleteModal.afterLeave}
        clientToDelete={deleteModal.data}
        open={deleteModal.isOpen}
        organizationId={organizationId}
        workspaceId={workspaceId}
        onClose={deleteModal.closeModal}
      />
    </DashboardContent>
  );
};
